import { createContext, useMemo, useState, type ReactNode } from 'react'
import { BASE_API } from '../api/enlace'
import type { components } from '../api/modelos'

/** Clave bajo la que vive el token. Exportada para que las pruebas no la adivinen. */
export const CLAVE_TOKEN = 'tactica.token'

const RUTA_LOGIN = `${BASE_API}/session/login`

type RespuestaLogin = components['schemas']['RespuestaLogin']

export interface UsuarioSesion {
  id: string
  email: string
}

/**
 * Lo que la aplicacion entiende por "estar dentro". Mantiene los nombres que
 * usaba la sesion de Supabase (`access_token`, `user.email`) porque son los que
 * leen `AppShell` y el cliente de API: el cambio de proveedor no tenia por que
 * propagarse a la interfaz.
 */
export interface Sesion {
  access_token: string
  user: UsuarioSesion
}

export interface ContextoSesion {
  sesion: Sesion | null
  cargando: boolean
  obtenerToken: () => Promise<string | null>
  entrar: (email: string, password: string) => Promise<void>
  salir: () => Promise<void>
}

export const contextoSesion = createContext<ContextoSesion | null>(null)

/** Lo minimo de `localStorage` que hace falta aqui; las pruebas inyectan un doble. */
export interface AlmacenTokens {
  getItem: (clave: string) => string | null
  setItem: (clave: string, valor: string) => void
  removeItem: (clave: string) => void
}

interface CargaToken {
  sub?: unknown
  email?: unknown
  exp?: unknown
}

/**
 * Lee el cuerpo del JWT **sin comprobar la firma**. No es un descuido: la firma
 * la verifica el servidor en cada peticion, y comprobarla aqui exigiria meter el
 * secreto en el bundle, que es justo lo que no debe pasar. Esto sirve solo para
 * no arrastrar tokens que ya sabemos muertos.
 */
function leerCarga(token: string): CargaToken | null {
  const partes = token.split('.')
  if (partes.length !== 3) return null
  try {
    const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/')
    const relleno = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const binario = atob(relleno)
    const bytes = Uint8Array.from(binario, (caracter) => caracter.charCodeAt(0))
    const carga: unknown = JSON.parse(new TextDecoder().decode(bytes))
    if (!carga || typeof carga !== 'object') return null
    return carga as CargaToken
  } catch {
    // Token manipulado, truncado o de otra epoca: se trata como si no hubiera.
    return null
  }
}

/** Devuelve la sesion que representa el token, o `null` si no sirve o ya caduco. */
function sesionDeToken(token: string): Sesion | null {
  const carga = leerCarga(token)
  if (!carga) return null
  const { sub, email, exp } = carga
  if (typeof sub !== 'string' || typeof email !== 'string' || typeof exp !== 'number') return null
  if (exp * 1000 <= Date.now()) return null
  return { access_token: token, user: { id: sub, email } }
}

/**
 * Recupera la sesion guardada y, de paso, tira lo que ya no vale. Borrar en
 * cuanto se detecta evita repetir el mismo descarte en cada recarga y, sobre
 * todo, evita mandar al servidor un token que sabemos caducado.
 */
function recuperarSesion(almacen: AlmacenTokens): Sesion | null {
  const token = almacen.getItem(CLAVE_TOKEN)
  if (!token) return null
  const sesion = sesionDeToken(token)
  if (!sesion) almacen.removeItem(CLAVE_TOKEN)
  return sesion
}

const MENSAJE_GENERICO = 'No se ha podido iniciar sesión. Inténtalo de nuevo.'

/**
 * Respaldo por estado para cuando el cuerpo no llega o no es el nuestro (un
 * proxy delante, una pagina de error HTML). El 429 tiene el suyo a proposito:
 * decirle "credenciales incorrectas" a quien esta bloqueado por intentos manda
 * a cambiar una contrasena que era correcta.
 */
const MENSAJES_POR_ESTADO: Record<number, string> = {
  401: 'Correo o contraseña incorrectos',
  429: 'Demasiados intentos. Espera unos minutos antes de volver a probar.',
}

async function mensajeDeFallo(respuesta: Response): Promise<string> {
  try {
    const cuerpo: unknown = await respuesta.json()
    const mensaje = (cuerpo as { error?: { mensaje?: unknown } })?.error?.mensaje
    if (typeof mensaje === 'string' && mensaje.trim()) return mensaje
  } catch {
    // Cuerpo no-JSON: nos quedamos con lo que dice el estado.
  }
  return MENSAJES_POR_ESTADO[respuesta.status] ?? MENSAJE_GENERICO
}

interface Props {
  children: ReactNode
  almacen?: AlmacenTokens
}

export function SesionProvider({ children, almacen = window.localStorage }: Props) {
  // La recuperacion es sincrona (localStorage), asi que se hace en el
  // inicializador y no en un efecto: no hay ni un fotograma de pantalla de
  // carga al recargar, que era el parpadeo que se queria evitar.
  const [sesion, setSesion] = useState<Sesion | null>(() => recuperarSesion(almacen))

  const valor = useMemo<ContextoSesion>(
    () => ({
      sesion,
      // Se mantiene en el contrato porque `RutaProtegida` y `Login` lo
      // consultan, pero ya no hay nada que esperar: la sesion se conoce en el
      // primer render.
      cargando: false,

      // Se relee del almacen en cada llamada en vez de servir el token en
      // memoria: asi una caducidad a media sesion se detecta antes de gastar
      // una peticion, y no despues de un 401.
      obtenerToken: async () => {
        const token = almacen.getItem(CLAVE_TOKEN)
        if (!token) return null
        if (!sesionDeToken(token)) {
          almacen.removeItem(CLAVE_TOKEN)
          setSesion(null)
          return null
        }
        return token
      },

      entrar: async (email, password) => {
        const respuesta = await fetch(RUTA_LOGIN, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ email, password }),
        })

        if (!respuesta.ok) throw new Error(await mensajeDeFallo(respuesta))

        const datos = (await respuesta.json()) as RespuestaLogin
        const nueva = sesionDeToken(datos.token)
        // Un token que no se entiende es un fallo del servidor, no del usuario,
        // pero guardarlo dejaria la interfaz "dentro" y toda peticion en 401.
        if (!nueva) throw new Error(MENSAJE_GENERICO)

        almacen.setItem(CLAVE_TOKEN, datos.token)
        setSesion(nueva)
      },

      salir: async () => {
        // El token es autocontenido: no hay nada que revocar en el servidor,
        // basta con dejar de tenerlo.
        almacen.removeItem(CLAVE_TOKEN)
        setSesion(null)
      },
    }),
    [sesion, almacen],
  )

  return <contextoSesion.Provider value={valor}>{children}</contextoSesion.Provider>
}
